/**
 * assets/dm-analytics.js — the theme's ONLY `Shopify.analytics.publish` call
 * site, and the consent gate in front of it (PLAN Phase 5 + §10.4; C §6.2).
 *
 * ── THIS IS A LEGAL CONTROL, NOT A CONVENIENCE ──────────────────────────────
 * Publishing before consent in a consent-required region is a legal defect, not
 * a bug. So nothing else in this theme calls `Shopify.analytics.publish`: every
 * event goes through `window.DM.track()`, which QUEUES until
 * `window.Shopify.customerPrivacy` resolves and flushes on
 * `visitorConsentCollected`. One call site, greppable, in this file.
 *
 * That object does not exist until Shopify's privacy script has loaded, and it
 * MAY NEVER EXIST — `Settings → Customer privacy` is an admin task, not theme
 * code. Every method is therefore feature-detected AND try/caught before it is
 * called: `analyticsProcessingAllowed()`, `marketingAllowed()`,
 * `shouldShowBanner()`. The API is requested with
 * `Shopify.loadFeatures(['consent-tracking-api'])` on a bounded retry, because
 * `loadFeatures` itself arrives with `content_for_header`'s async bundle and is
 * not there on the first tick.
 *
 * THE THREE STATES, and what each does to the queue:
 *   allow  `analyticsProcessingAllowed() === true` → drain, then publish live.
 *   wait   the API is not there yet, OR it says no while `shouldShowBanner()`
 *          is still true — the visitor has been ASKED and has not answered. An
 *          unanswered question is not a refusal. The queue holds (capped,
 *          oldest dropped) and nothing is sent.
 *   deny   it says no and no banner is showing — answered, or denied by region
 *          default. The queue is DISCARDED, never replayed. A later
 *          `visitorConsentCollected` re-evaluates and new events flow.
 * With no privacy API at all the state is `wait` forever and this file sends
 * NOTHING. Deliberate: an unconfigured consent layer must fail CLOSED. Add
 * `?dm_analytics_debug` to any URL to watch the decision in the console.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────────
 * · No GA4/Meta/TikTok tag. The theme fires no third-party tag directly; they
 *   live in Customer Events, where Shopify enforces consent per pixel.
 * · No Consent Mode v2 mapping. `analytics_storage` ← analytics and
 *   `ad_storage` / `ad_user_data` / `ad_personalization` ← marketing are
 *   implemented INSIDE the Google channel / custom pixel, NOT in the theme
 *   (PLAN §10.4). `consent_marketing` rides on every payload so the pixel can
 *   key that mapping off the same read; this file never sets a signal.
 * · No money formatting. `cart_value_before_add` and `free_shipping_gap` are
 *   integer CENTS (C §6.2). A formatted amount is a display artefact, useless
 *   as a metric — and money is Liquid's job, never JavaScript's.
 * · No `fetch`, and above all no `fetch('/cart/add')`. Every add on this site
 *   routes through Dawn's `<product-form>`; this file only LISTENS.
 *
 * ── `atc_source` — THE VOCABULARY (C §6.2), OWNED HERE ──────────────────────
 * Which of the ~8 add surfaces earned the add. Several units emit it, so the
 * closed set lives in one place:
 *   buy_box · sticky_bar · pdp_pair_with · pdp_sets · cart_crosssell ·
 *   quick_add_home · quick_add_collection · lp_callout · lp_sticky
 * plus the sentinel `unattributed` — a NAMED unknown, because a wrong bucket is
 * worse than an honest one. HOW A SURFACE DECLARES ITSELF, in priority order:
 *   1. `data-dm-atc-source="…"` on the submit button or any ancestor of it;
 *   2. the submitter sitting inside `<dm-sticky-atc>` → `sticky_bar`. The one
 *      structural fact this file reads for itself, and it must outrank (3):
 *      the sticky button is `form=`-linked to the BUY BOX's form and would
 *      otherwise inherit the buy box's own attribute;
 *   3. `data-dm-atc-source` on the `<form>`;
 *   4. `window.DM.analytics.source('…')` claimed within 15s — the escape hatch
 *      for a surface that cannot carry an attribute;
 *   5. `unattributed`.
 * A value outside the set still publishes if it is machine-value shaped, with a
 * `console.warn`: losing the datum is worse than reporting the drift.
 *
 * ── THE TWO CENTS FIGURES ARE READ, NEVER RECOMPUTED ────────────────────────
 * D-18's literal rate condition (after discounts, excluding shipping, taxes and
 * gift cards) is stated in exactly ONE place — `dm-shipping-progress.liquid` —
 * and this file reads its published attributes rather than restating it:
 *   `[data-gap-cents]`      the remaining gap, cents. That element renders only
 *                           when a threshold is configured, so ABSENT means the
 *                           parameter is OMITTED, never sent as 0 (D-18: with
 *                           no threshold the bar is absent, not at 0%). A
 *                           present `0` means the threshold is exactly met, and
 *                           that distinction is the whole point.
 *   `[data-dm-cart-total]`  `cart.total_price` in cents, emitted by
 *                           `snippets/dm-cart-count.liquid` — the header bag's
 *                           subtree, which renders on every page INCLUDING an
 *                           empty cart (the first add, from 0, is the one that
 *                           most needs a `cart_value_before_add`) AND is
 *                           re-rendered by Dawn's cart runtime on every AJAX
 *                           update, so the second add on a page reads the
 *                           post-first-add total rather than a stale one.
 *                           Absent only where the merchant hides the bag;
 *                           `DM.analytics.configure({ cart_total })` is the
 *                           seam for that case, and nothing calls it today.
 * Both are snapshotted at `submit`, i.e. BEFORE the add, which is what makes
 * `cart_value_before_add` mean what its name says.
 *
 * ── ORDERING — TWO CONSTRAINTS, NOT ONE ─────────────────────────────────────
 * 1. AFTER `dm-core.js`: dm-core assigns `window.DM = { lazy }` WHOLESALE, so
 *    loading this first would have `DM.track` silently deleted.
 * 2. AFTER `constants.js` + `pubsub.js`: `bindDawn()` needs `subscribe` and
 *    `PUB_SUB_EVENTS`, and in `layout/theme.liquid` those are emitted AFTER
 *    dm-core.js. Satisfying (1) alone by sitting immediately below dm-core
 *    therefore used to satisfy (2) NOT AT ALL, and the failure was silent and
 *    total: `bindDawn()` returned early, never retried, and not one
 *    `dm_add_to_cart` was ever published. Two defences now, because an
 *    ordering rule enforced only by a comment is not enforced:
 *      · the `<script>` tag is emitted below `global.js`, satisfying both;
 *      · `bindDawn()` is idempotent and is re-attempted on DOMContentLoaded.
 *        Every Dawn script is `defer`, so DOMContentLoaded is the first moment
 *        at which all of them have run, in ANY emission order.
 *
 * Classic script, ES2020. The only dependencies are Dawn's `subscribe` /
 * `PUB_SUB_EVENTS` script-scope globals, both feature-detected.
 */
(function () {
  'use strict';

  if (window.DM && window.DM.analytics) return;

  const SOURCES = [
    'buy_box',
    'sticky_bar',
    'pdp_pair_with',
    'pdp_sets',
    'cart_crosssell',
    'quick_add_home',
    'quick_add_collection',
    'lp_callout',
    'lp_sticky',
    'unattributed',
  ];
  const SOURCE_SET = new Set(SOURCES);
  /** A machine value, never copy: lowercase, snake_case, bounded. */
  const SOURCE_SHAPE = /^[a-z][a-z0-9_]{0,39}$/;

  const QUEUE_MAX = 50; // a page whose consent never resolves must not grow
  const CLAIM_TTL = 15000; // DM.analytics.source() → submit
  const ADD_TTL = 30000; // submit → cartUpdate
  const TICK_MS = 300;
  const TICKS_MAX = 34; // ~10s, then stop polling and wait for the event

  const DEBUG = /[?&]dm_analytics_debug/.test(window.location.search);
  const log = function () {
    if (DEBUG) console.info.apply(console, ['dm-analytics'].concat([].slice.call(arguments)));
  };

  /* ── 1. CONSENT ─────────────────────────────────────
     Every read is guarded twice: the method may not exist, and it may throw
     while the privacy bundle is half-initialised. `null` means UNKNOWABLE,
     which is not the same as `false` and must never be treated as consent.
     -------------------------------------------------- */

  function privacy() {
    const p = window.Shopify && window.Shopify.customerPrivacy;
    return p && typeof p.analyticsProcessingAllowed === 'function' ? p : null;
  }

  function ask(p, method) {
    if (!p || typeof p[method] !== 'function') return null;
    try {
      return p[method]() === true;
    } catch (error) {
      return null;
    }
  }

  function state() {
    const p = privacy();
    if (!p) return 'wait';
    const allowed = ask(p, 'analyticsProcessingAllowed');
    if (allowed === true) return 'allow';
    if (allowed === null) return 'wait';
    return ask(p, 'shouldShowBanner') === true ? 'wait' : 'deny';
  }

  function consent() {
    const p = privacy();
    return { analytics: ask(p, 'analyticsProcessingAllowed'), marketing: ask(p, 'marketingAllowed') };
  }

  let requested = false;

  function requestPrivacy() {
    if (requested || privacy()) return;
    const S = window.Shopify;
    if (!S || typeof S.loadFeatures !== 'function') return; // not up yet; next tick
    requested = true;
    try {
      S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () {
        log('privacy api resolved', state());
        flush();
      });
    } catch (error) {
      requested = false;
    }
  }

  /* ── 2. THE QUEUE ─────────────────────────────────── */

  const queue = [];
  const config = {};
  let dropped = 0;
  let ticks = 0;
  let timer = null;

  function emit(entry) {
    const analytics = window.Shopify && window.Shopify.analytics;
    if (!analytics || typeof analytics.publish !== 'function') return false;
    const waited = Date.now() - entry.at;
    // `consent_analytics` is not sent: by construction it is always true here.
    // `consent_wait_ms` is, because it is the evidence that the gate held.
    const payload = Object.assign({}, entry.params, { consent_marketing: consent().marketing === true });
    if (waited > 0) payload.consent_wait_ms = waited;
    try {
      analytics.publish(entry.name, payload);
      log('published', entry.name, payload);
    } catch (error) {
      console.error('dm-analytics', error);
    }
    return true;
  }

  function flush() {
    const now = state();
    if (now === 'wait') return;
    if (now === 'deny') {
      if (queue.length) log('denied — discarding', queue.length);
      queue.length = 0;
      return;
    }
    while (queue.length) {
      if (!emit(queue[0])) {
        poll(); // consent is in, but the analytics bundle is not up yet
        return;
      }
      queue.shift();
    }
  }

  function poll() {
    if (timer) return;
    ticks = 0; // every fresh wait gets its own budget
    timer = setInterval(function () {
      ticks += 1;
      requestPrivacy();
      flush();
      if (ticks >= TICKS_MAX || (state() !== 'wait' && queue.length === 0)) {
        clearInterval(timer);
        timer = null;
      }
    }, TICK_MS);
  }

  /** Scalars only. An object in a pixel payload is a silent serialisation
   *  failure, and dropping `undefined` is how an unresolved parameter
   *  DISAPPEARS rather than becoming the string "undefined". */
  function scalars(params) {
    const out = {};
    for (const key in params) {
      if (!Object.prototype.hasOwnProperty.call(params, key)) continue;
      const value = params[key];
      if (value === undefined || value === null || value === '') continue;
      const type = typeof value;
      if (type === 'string' || type === 'boolean' || (type === 'number' && Number.isFinite(value))) out[key] = value;
    }
    return out;
  }

  function track(name, params) {
    if (typeof name !== 'string' || name === '') return false;
    const entry = { name: name, params: scalars(params), at: Date.now() };
    const now = state();
    if (now === 'deny') {
      log('denied', name);
      return false;
    }
    if (now === 'allow' && queue.length === 0 && emit(entry)) return true;
    queue.push(entry);
    if (queue.length > QUEUE_MAX) {
      queue.shift();
      dropped += 1;
    }
    log('queued', name, now);
    poll();
    return false;
  }

  /* ── 3. CONTEXT — read, never computed ───────────── */

  function attrCents(selector, attribute) {
    const el = document.querySelector(selector);
    const raw = el ? el.getAttribute(attribute) : null;
    if (raw === null || raw === '') return undefined;
    const value = Number(raw);
    return Number.isFinite(value) ? Math.round(value) : undefined;
  }

  /** `dm-shipping-progress` renders nothing without a threshold, so `undefined`
   *  here is D-18's "absent, not 0" travelling all the way to the pixel. */
  function shippingGap() {
    return attrCents('[data-gap-cents]', 'data-gap-cents');
  }

  function cartTotal() {
    const live = attrCents('[data-dm-cart-total]', 'data-dm-cart-total');
    if (live !== undefined) return live;
    return Number.isFinite(config.cart_total) ? Math.round(config.cart_total) : undefined;
  }

  function template() {
    const main = document.querySelector('main[data-template]');
    return (main && main.getAttribute('data-template')) || '';
  }

  /** The merchant-authored plan name, taken from the option the shopper is
   *  actually on. Never synthesised — the string is the subscription app's. */
  function planLabel(select, id) {
    if (!select || !select.options) return '';
    for (let i = 0; i < select.options.length; i += 1) {
      if (String(select.options[i].value) === String(id)) return select.options[i].text.trim();
    }
    return '';
  }

  /* ── 4. atc_source ───────────────────────────────── */

  let claimed = null;

  function normalise(value) {
    const v = String(value == null ? '' : value).trim();
    if (!v || !SOURCE_SHAPE.test(v)) return null;
    if (!SOURCE_SET.has(v)) console.warn('dm-analytics: atc_source "' + v + '" is outside the documented vocabulary');
    return v;
  }

  function source(value) {
    const v = normalise(value);
    if (v) claimed = { source: v, at: Date.now() };
    return v;
  }

  function declared(node) {
    const owner = node && node.closest ? node.closest('[data-dm-atc-source]') : null;
    return owner ? normalise(owner.getAttribute('data-dm-atc-source')) : null;
  }

  function resolveSource(form, submitter) {
    return (
      declared(submitter) ||
      (submitter && submitter.closest && submitter.closest('dm-sticky-atc') ? 'sticky_bar' : null) ||
      declared(form) ||
      (claimed && Date.now() - claimed.at < CLAIM_TTL ? claimed.source : null) ||
      'unattributed'
    );
  }

  /* ── 5. THE ADD ───────────────────────────────────────
     Snapshot at `submit` (CAPTURE, so it runs before Dawn's own handler),
     publish on `cartUpdate`. The snapshot is what makes "before" true; the
     pubsub event is what makes it a real add rather than an intent.
     -------------------------------------------------- */

  let pendingAdd = null;

  function isAddForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    if (form.getAttribute('data-type') === 'add-to-cart-form') return true;
    return (form.getAttribute('action') || '').indexOf('/cart/add') > -1;
  }

  function snapshot(form, submitter) {
    let fields;
    try {
      fields = new FormData(form);
    } catch (error) {
      return;
    }
    // A disabled control is excluded from FormData — which IS the "omit
    // `selling_plan`, never send 0" contract, read back here rather than
    // re-derived from which radio looks checked.
    const plan = String(fields.get('selling_plan') || '');
    const owner = form.closest ? form.closest('[data-product-id]') : null;
    pendingAdd = {
      at: Date.now(),
      params: {
        atc_source: resolveSource(form, submitter),
        purchase_option: plan ? 'subscription' : 'one_time',
        selling_plan_id: plan,
        selling_plan_name: plan ? planLabel(form.elements ? form.elements.selling_plan : null, plan) : '',
        variant_id: String(fields.get('id') || ''),
        product_id: owner ? owner.getAttribute('data-product-id') : '',
        quantity: Number(fields.get('quantity')) || 1,
        cart_value_before_add: cartTotal(),
        free_shipping_gap: shippingGap(),
        template: template(),
      },
    };
    claimed = null;
  }

  function claimAdd() {
    const snap = pendingAdd;
    pendingAdd = null;
    return snap && Date.now() - snap.at <= ADD_TTL ? snap.params : null;
  }

  document.addEventListener(
    'submit',
    function (event) {
      // `SubmitEvent.submitter` is the correct source and is what the priority
      // order is written against. `activeElement` covers the browsers that do
      // not implement it: a pressed button is the focused element, so the
      // sticky-bar case still resolves instead of inheriting the buy box's tag.
      if (isAddForm(event.target)) snapshot(event.target, event.submitter || document.activeElement);
    },
    true
  );

  /* ── 6. DAWN'S CHANNELS ──────────────────────────── */

  const lastOption = new Map();
  let lastVariant = '';
  let bound = false;

  /** Idempotent and retryable — see ORDERING above. Returns whether Dawn's
   *  pubsub channels are subscribed, so the caller can decide to try again. */
  function bindDawn() {
    if (bound) return true;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
    bound = true;

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || event.source !== 'product-form') return;
      track(
        'dm_add_to_cart',
        claimAdd() || {
          atc_source: 'unattributed',
          variant_id: String(event.productVariantId || ''),
          template: template(),
        }
      );
    });

    subscribe(PUB_SUB_EVENTS.cartError, function (event) {
      if (!event || event.source !== 'product-form') return;
      const params = claimAdd() || { atc_source: 'unattributed', template: template() };
      params.variant_id = String(event.productVariantId || params.variant_id || '');
      track('dm_add_to_cart_failed', params);
    });

    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      const variant = event && event.data && event.data.variant;
      if (!variant || String(variant.id) === lastVariant) return;
      lastVariant = String(variant.id);
      track('dm_variant_selected', {
        variant_id: lastVariant,
        item_variant: variant.title || '',
        template: template(),
      });
    });

    log('dawn channels bound');
    return true;
  }

  // `dm-purchase-options` dispatches on EVERY resolve, including the one at
  // first paint and the ones a variant change forces. The first state per
  // section is therefore a baseline and is never published; only a genuine
  // change is (C §6.2 `dm_purchase_option_changed`).
  document.addEventListener('dm:purchase-option-change', function (event) {
    const detail = event.detail || {};
    const section = String(detail.sectionId || '');
    const planId = detail.sellingPlanId ? String(detail.sellingPlanId) : '';
    const key = detail.mode + '|' + planId;
    const seen = lastOption.get(section);
    lastOption.set(section, key);
    if (seen === undefined || seen === key) return;
    const select = event.target && event.target.querySelector ? event.target.querySelector('[data-dm-po-plan]') : null;
    track('dm_purchase_option_changed', {
      purchase_option: detail.mode === 'subscribe' ? 'subscription' : 'one_time',
      selling_plan_id: planId,
      selling_plan_name: planId ? planLabel(select, planId) : '',
      variant_id: String(detail.variantId || ''),
      template: template(),
    });
  });

  /* ── 7. WIRING ───────────────────────────────────── */

  // Shopify dispatches this on `document`; bound on `window` too, because the
  // one thing that must not rest on a documentation detail is the flush.
  function collected() {
    log('consent collected', consent());
    flush();
  }
  document.addEventListener('visitorConsentCollected', collected);
  window.addEventListener('visitorConsentCollected', collected);

  const DM = (window.DM = window.DM || {});
  DM.track = track;
  DM.analytics = {
    track: track,
    source: source,
    /** Optional Liquid seed, in CENTS: `{ cart_total: {{ cart.total_price }} }`.
     *  The live DOM hook always wins; this is the first-paint fallback. */
    configure: function (next) {
      if (next && typeof next === 'object') Object.assign(config, next);
      return config;
    },
    consent: consent,
    state: state,
    debug: function () {
      return { state: state(), queued: queue.length, dropped: dropped, consent: consent() };
    },
    SOURCES: Object.freeze(SOURCES.slice()),
  };

  // If Dawn's pubsub bundle has not run yet, DOMContentLoaded is the guaranteed
  // second chance: every one of those scripts is `defer`. `once` because a
  // bound file must not keep a listener alive for nothing.
  if (!bindDawn()) document.addEventListener('DOMContentLoaded', bindDawn, { once: true });
  requestPrivacy();
  poll();
})();
